/** Load Google Identity Services script once (Sign in with Google / One Tap client). */

const GSI_SRC = "https://accounts.google.com/gsi/client";

let gsiLoadPromise: Promise<void> | null = null;

function findGsiScript(): HTMLScriptElement | null {
  return document.querySelector(`script[src="${GSI_SRC}"]`);
}

/** Script `onload` can fire before `google.accounts.id` is attached — wait until it exists. */
function waitForAccountsId(timeoutMs = 15000): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const check = (): void => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error("Google Sign-In API did not become ready"));
        return;
      }
      window.setTimeout(check, 32);
    };
    check();
  });
}

export function loadGoogleGsiScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiLoadPromise) return gsiLoadPromise;

  gsiLoadPromise = new Promise((resolve, reject) => {
    const fail = (err: Error): void => {
      gsiLoadPromise = null;
      reject(err);
    };

    const succeed = (): void => {
      resolve();
    };

    const existing = findGsiScript();
    if (existing) {
      void waitForAccountsId()
        .then(succeed)
        .catch((e: unknown) => fail(e instanceof Error ? e : new Error(String(e))));
      return;
    }

    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      void waitForAccountsId()
        .then(succeed)
        .catch((e: unknown) => {
          s.remove();
          fail(e instanceof Error ? e : new Error(String(e)));
        });
    };
    s.onerror = () => {
      s.remove();
      fail(new Error("Could not load Google Sign-In"));
    };
    document.head.appendChild(s);
  });

  return gsiLoadPromise;
}

/** Clears cached loader state so the next call injects a fresh GSI script (e.g. user clicks Retry). */
export function resetGoogleGsiLoader(): void {
  gsiLoadPromise = null;
  findGsiScript()?.remove();
}
